import React, { useCallback, useState } from "react";
import { isBlank, notBlank } from "../../util/ext";
import { Box, List, ListItem, ListItemText } from "@mui/material";
import Typography from "@mui/material/Typography";
import {
  flatten,
  identity,
  ifElse,
  length,
  map,
  mergeRight,
  omit,
  pipe,
  pluck,
  reject,
  values,
} from "ramda";

const useErrorBox = () => {
  const [errors, setErrors] = useState({});

  const addValidationError = useCallback(validationError => {
    setErrors(prev =>
      mergeRight(prev, {
        validation: mergeRight(prev.validation, validationError),
      }),
    );
  }, []);

  const removeValidationError = useCallback(field => {
    setErrors(prev =>
      mergeRight(prev, { validation: omit([field], prev.validation) }),
    );
  }, []);

  const removeValidationErrors = useCallback(() => {
    setErrors(prev => mergeRight(prev, { validation: {} }));
  }, []);

  const setGeneralError = useCallback(err => {
    setErrors(prev => mergeRight(prev, { general: err }));
  }, []);

  const removeGeneralErrors = useCallback(err => {
    setErrors(prev => mergeRight(prev, { general: {} }));
  }, []);

  const hasGeneralError = useCallback(() => notBlank(errors.general), [
    errors.general,
  ]);

  const setValidationErrors = useCallback(validationErrors => {
    setErrors(prev => mergeRight(prev, { validation: validationErrors }));
  }, []);

  const hasValidationError = useCallback(
    field => notBlank(errors.validation) && notBlank(errors.validation[field]),
    [errors.validation],
  );

  const firstValidationError = useCallback(
    field =>
      notBlank(errors.validation) && notBlank(errors.validation[field])
        ? errors.validation[field][0]
        : "",
    [errors.validation],
  );

  const resetErrors = useCallback(() => setErrors({}), []);

  const WrappedErrorBox = React.forwardRef((props, ref) => {
    const { showValidationErrors, ...otherProps } = props;
    if (notBlank(errors)) {
      return (
        <Box {...otherProps} ref={ref}>
          {notBlank(errors.validation) && (
            <Box>
              <Typography color="error" variant="body1" component="div">
                Please correct all of the below errors.
              </Typography>

              {showValidationErrors && (
                <Box pl={2}>
                  <List dense>
                    {map(
                      msg => (
                        <ListItem key={msg}>
                          <ListItemText
                            primary={"- " + msg}
                            primaryTypographyProps={{ color: "error" }}
                          />
                        </ListItem>
                      ),
                      flatten(values(errors.validation)),
                    )}
                  </List>
                </Box>
              )}
            </Box>
          )}

          {notBlank(errors.general) && notBlank(errors.general.body) && (
            <Box>
              <List dense>
                {map(
                  msg => (
                    <ListItem key={msg}>
                      <ListItemText
                        primary={msg}
                        primaryTypographyProps={{ color: "error" }}
                      />
                    </ListItem>
                  ),
                  pipe(
                    pluck("message"),
                    reject(isBlank),
                    ifElse(length, identity, () => [
                      errors.general.body.message,
                    ]),
                  )(errors.general.body.errors),
                )}
              </List>
            </Box>
          )}

          {notBlank(errors.general) &&
            isBlank(errors.general.body) &&
            notBlank(errors.general.message) && (
              <Typography color="error" variant="body1" component="div">
                {errors.general.message}
              </Typography>
            )}
        </Box>
      );
    } else {
      return null;
    }
  });

  return {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    removeValidationErrors,
    setGeneralError,
    removeGeneralErrors,
    hasGeneralError,
    hasValidationError,
    firstValidationError,
    resetErrors,
    ErrorBox: WrappedErrorBox,
  };
};

export { useErrorBox };
